import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { buildApiUrl, buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import './PositionDetailsTable.css';
import { useWorkspace } from './workspace/WorkspaceContext';
import { isTrader } from './auth/authUtils';
import TradingViewChartIcon from './TradingViewChartIcon';

// Smart price formatter (handles micro values like 0.000000005)
const formatPrice = (price) => {
  if (!price || isNaN(price)) return '';
  return price < 0.0001 ? price.toFixed(8) : price.toFixed(4);
};

// Correctly computes SR vs Avg Open % considering trade direction
const computeSrChangePct = (open, srLevel, holdSide) => {
  if (!open || !srLevel) return null;
  const sr = parseFloat(srLevel);
  const baseChange = ((sr - open) / open) * 100;
  // invert for short trades
  const adjusted = holdSide === 'short' ? -baseChange : baseChange;
  return Number(adjusted.toFixed(2)); // always returns a number
};




Modal.setAppElement('#root');

function PriceInfo({ symbol, displayedData, srLevel }) {
  if (!symbol) return null;
  const pos = displayedData.find((p) => p.symbol === symbol);
  if (!pos) return null;

  const open = parseFloat(pos.averageOpenPrice || 0);
  const market = parseFloat(pos.marketPrice || 0);
  if (!open || !market) return null;

  const marketChange = ((market - open) / open) * 100;
  const marketChangeColor = marketChange >= 0 ? 'positive' : 'negative';

  // SR level comparison
const srChangePct =
  srLevel && parseFloat(srLevel)
    ? computeSrChangePct(open, parseFloat(srLevel), pos.holdSide)
    : null;

  const srChangeColor =
    srChangePct !== null && srChangePct >= 0 ? 'positive' : 'negative';

  return (
    <div className="price-info">
      <div className="price-info-row">
        <span className="price-info-label">Avg&nbsp;Open:</span>
        <span className="price-info-value">{formatPrice(open)}</span>
      </div>
      <div className="price-info-row">
        <span className="price-info-label">Market&nbsp;Price:</span>
        <span className="price-info-value">{formatPrice(market)}</span>
      </div>
      <div className="price-info-row">
        <span className="price-info-label">Change:</span>
        <span className={`price-info-change ${marketChangeColor}`}>
          {marketChange.toFixed(2)}%
        </span>
      </div>
      {srLevel && (
        <div className="price-info-row">
          <span className="price-info-label">SR vs Open:</span>
          <span className={`price-info-change ${srChangeColor}`}>
            {srChangePct}%
          </span>
        </div>
      )}

    </div>
  );
}


function PositionDetailsTable() {
  const { workspace } = useWorkspace();
  const canClosePositions = isTrader();
  const [positions, setPositions] = useState([]);
  const [positionsNotice, setPositionsNotice] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'unrealizedPL', direction: 'desc' });
  const [positionRefreshTrigger, setpositionRefreshTrigger] = useState(0);
  const [loadingClose, setLoadingClose] = useState(false);
  const [closeResponse, setCloseResponse] = useState(null);
  const [modalPhase, setModalPhase] = useState('confirm');  
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
 // New Stop Loss SR section states
 const [addTrailingSrFlag, setAddTrailingSrFlag] = useState(false);
 const [trailingSrLevel, setTrailingSrLevel] = useState('');
 const [trailingSrTimeFrame, setTrailingSrTimeFrame] = useState('');

 const [addBreakEvenSrFlag, setAddBreakEvenSrFlag] = useState(false);
 const [breakEvenSrLevel, setBreakEvenSrLevel] = useState('');
 const [breakEvenSrTimeFrame, setBreakEvenSrTimeFrame] = useState('');
 const [selectedSrSymbol, setSelectedSrSymbol] = useState('');
 const [applyBreakEvenToAll, setApplyBreakEvenToAll] = useState(false);
const [breakEvenResultSummary, setBreakEvenResultSummary] = useState({ success: [], failed: [] });
const [trailingSrResultSummary, setTrailingSrResultSummary] = useState({ success: [], failed: [] });

const [trailingSrChangePct, setTrailingSrChangePct] = useState(null);
const [breakEvenSrChangePct, setBreakEvenSrChangePct] = useState(null);
const computeChangePct = (market, open, side) => {
  if (!market || !open) return null;

  const change = ((market - open) / open) * 100;
  const adjusted = side === 'short' ? -change : change;

  return adjusted;
};

const [tpTrackers, setTpTrackers] = useState([]);
const [expandedSymbols, setExpandedSymbols] = useState(new Set());
const [showTpTrackers, setShowTpTrackers] = useState(false);
const [hideAllTrackers, setHideAllTrackers] = useState(false);

const [markPrice, setMarkPrice] = useState(null);
const [srChangePct, setSrChangePct] = useState(null);

const computeSrChangePct = (open, sr) => {
  if (!open || !sr) return null;
  const pct = ((sr - open) / open) * 100;
  return Number(pct.toFixed(2));
};

const [tpSrChangePct, setTpSrChangePct] = useState(null);


const resetSrSection = () => {
  setAddTrailingSrFlag(false);
  setTrailingSrLevel('');
  setTrailingSrTimeFrame('');

  setAddBreakEvenSrFlag(false);
  setBreakEvenSrLevel('');
  setBreakEvenSrTimeFrame('');
  setApplyBreakEvenToAll(false);

  setSelectedSrSymbol('');
};


  const [pendingSymbols, setPendingSymbols]     = useState([]);
  const [filteredBySelectionKeys, setFilteredBySelectionKeys] = useState([]);
  const [closeMode, setCloseMode] = useState('immediate'); // 'immediate' or 'tracker'
    // New TP tracker UI state
  const [tpBase, setTpBase] = useState('SR_LEVEL');                       // 'EMA' | 'VWAP' | 'SWING_HIGH_LOW' | 'CANDLE_COLOR' | 'RSI_AND_SRSI_REVERSAL'
  const [tpValue, setTpValue] = useState('');                        // concrete selection (e.g., EMA200, DAILY_VWAP, RED, etc.)
  const [tpCross, setTpCross] = useState('FIRST_CANDLE_CROSS');      // 'FIRST_CANDLE_CROSS' | 'CONFIRMED_CANDLE_CROSS'

  const [selectedTimeFrame, setSelectedTimeFrame] = useState('');


const timeFrameOptions = [ '5', '15', '30', '60', '240', '480', '720', '1440'];

const TP_BASE_OPTIONS = [
  'SR_LEVEL',
  "EMA",
  "CANDLE_TREND_REVERSAL",
  "PREV_OPP_CANDLE_CLOSE_SL",
  "PREV_CANDLE_TREND_REVERSAL_SL",
  "PREV_LES_EDGE_CROSS_SL",
  "PROFIT_PERCENT",
  "SL_PERCENT",
  "SWING_LEVEL_STOCH_RSI_REVERSAL",
  "RSI_DIVERGENCE",
  "AO_DIVERGENCE",
  "FVG_TAGGED",
  "CANDLE_COLOR",
  "VWAP",
  "RSI_AND_SRSI_REVERSAL",
  "SWING_HIGH_LOW",
];

const [tpTrackerValue, setTpTrackerValue] = useState('');
const [trackerSpecialIdentifier, setTrackerSpecialIdentifier] = useState('');
 const [targetPercentage, setTargetPercentage] = useState('');
 const [targetPctAdjustment, setTargetPctAdjustment] = useState('');




const EMA_OPTIONS = [
  'EMA50_BAND',
  'EMA50_BAND_TREND_ALIGNED',
  'EMA200_BAND',
  'EMA200_BAND_CROSS_TREND_ALIGNED',
  'EMA50_BAND_CHOPPY_RANGE_BREAK_OUT',
  'EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_WITH_VOLUME',
  'EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_TREND_ALIGNED',
  'EMA12','EMA21','EMA12EMA21','EMA36','EMA21EMA36',
  'EMA50','EMA100','EMA200','ALL_EMA_STACK_TOP_EMA',
  'ALL_EMA_STACK_BOTTOM_EMA','LOWER_LENGTH_EMA_STACK_TOP_EMA',
  'LOWER_LENGTH_EMA_STACK_BOTTOM_EMA'
];
const CROSS_HIDDEN_EMA_OPTIONS = [
  'EMA50_BAND',
  'EMA50_BAND_TREND_ALIGNED',
  'EMA200_BAND',
  'EMA200_BAND_CROSS_TREND_ALIGNED',
  'EMA50_BAND_CHOPPY_RANGE_BREAK_OUT',
  'EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_WITH_VOLUME',
  'EMA50_BAND_CHOPPY_RANGE_BREAK_OUT_TREND_ALIGNED',
];
const VWAP_OPTIONS = ['DAILY_VWAP','WEEKLY_VWAP'];
const SWING_OPTIONS = ['SWING_HIGH','SWING_LOW','RECENT_SWING_HIGH','RECENT_SWING_LOW','SWING_HIGH_WICK','SWING_LOW_WICK'];
const CANDLE_COLOR_OPTIONS = ['RED','GREEN'];


const getTradeSideForSymbol = (symbol) => {
  const pos = positions.find((p) => p.symbol === symbol);
  return pos ? pos.holdSide === 'long' ? 'open_long' : 'open_short' : '';
};

const toTradeSide = (holdSide) => {
  if (holdSide === 'long') return 'open_long';
  if (holdSide === 'short') return 'open_short';
  return '';
};

const trackerGroupKey = (symbol, tradeSide) => `${symbol || ''}|${tradeSide || ''}`;


// New state for Multi-Select mode:
const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
const [selectedKeys, setSelectedKeys] = useState([]);

// Helper to toggle a rowÃ¢â‚¬â„¢s key in/out of selectedKeys:
const toggleSelection = (rowKey) => {
  setSelectedKeys(prev => {
    if (prev.includes(rowKey)) {
      // remove it
      return prev.filter(k => k !== rowKey);
    } else {
      // add it
      return [...prev, rowKey];
    }
  });
};

  const headers = {
    symbol: 'Symbol',
    chart: 'Chart',
    holdSide: 'Side',
    unrealizedPL: 'Unrealized P/L',
    margin: 'Margin',
    marketPrice: 'Market Price',
    averageOpenPrice: 'Average Open',
    changePct: "Change %",
    achievedProfits: 'Achieved Profits',
    total: 'Total',
    locked: 'Locked',
    available: 'Available',
    leverage: 'Leverage'
  };
  

    useEffect(() => {
      const intervalId = setInterval(() => {
        setpositionRefreshTrigger(prev => prev + 1); 
      }, 10000); // 10 seconds
    
      return () => clearInterval(intervalId); 
    }, []);

  const handleCloseFilteredPositions = async () => {
          if (displayedData.length === 0) {
            alert('No positions to close');
            return;
          }

          const isMultiSelectFiltered = filteredBySelectionKeys.length > 0;
          const isClosingAllPositions = displayedData.length === positions.length;

          if (!isMultiSelectFiltered && isClosingAllPositions) {
            const confirmed = window.confirm(
              'All Positions will be closed. Confirm you are fine to go ahead, or cancel and choose positions using Multi Select option.'
            );

            if (!confirmed) {
              return;
            }
          }

          // stash the symbols & drop into modal
          setPendingSymbols(displayedData.map(p => p.symbol));
          setConfirmModalOpen(true);
    };

    const fetchTpTrackers = async () => {
      try {
        const res = await fetch(
          buildScopedApiUrl('/api/bot/ui/get/getSignalRequestTaggedTPTrackers', workspace),
          {
            headers: getApiHeaders()
          });
        const data = await res.json();
        const normalizedTrackers = ensureArray(data);

        setTpTrackers(normalizedTrackers);
        setShowTpTrackers(normalizedTrackers.length > 0);

        // Ã¢Å“â€¦ Automatically expand all trackers on first load
        const allTrackerKeys = new Set(
          normalizedTrackers
            .map((t) => trackerGroupKey(t.symbol, t.tradeSide))
            .filter((key) => key !== '|')
        );
        setExpandedSymbols(allTrackerKeys);
        setHideAllTrackers(false); // ensure label syncs correctly
      } catch (err) {
        console.error('Error fetching TP trackers', err);
        alert('Failed to fetch Take Profit Trackers');
      }
    };


    const groupedTpTrackers = tpTrackers.reduce((acc, t) => {
      if (!t.symbol) return acc;
      const key = trackerGroupKey(t.symbol, t.tradeSide);
      if (!acc[key]) acc[key] = [];
      acc[key].push(t);
      return acc;
    }, {});

    const toggleSymbolExpansion = (symbol, tradeSide) => {
      const key = trackerGroupKey(symbol, tradeSide);
      setExpandedSymbols(prev => {
        const updated = new Set(prev);
        if (updated.has(key)) updated.delete(key);
        else updated.add(key);
        return updated;
      });
    };
    


  const onChangeTpBase = (e) => {
    const base = e.target.value;
    setTpBase(base);
    setTpValue(base === 'RSI_AND_SRSI_REVERSAL' || base === 'PREV_OPP_CANDLE_CLOSE_SL' || base === 'PREV_CANDLE_TREND_REVERSAL_SL' || base === 'PREV_LES_EDGE_CROSS_SL'
       ? base : '');
    setTrackerSpecialIdentifier('');
  };



const onModalCancel = () => {
  setConfirmModalOpen(false);
  setPendingSymbols([]);
};

// Build takeProfitType:
// - EMA/VWAP/SWING_HIGH_LOW => VALUE + CROSS + DIR (e.g., EMA200_FIRST_CANDLE_BEAR_CROSS)
// - CANDLE_COLOR/RSI_AND_SRSI_REVERSAL => just VALUE
const buildTpType = (base, value, crossMode, tradeSide) => {
  if (!base) return '';

  // Ã°Å¸â€Â¹ Auto-resolve for CANDLE_TREND_REVERSAL
  if (base === 'CANDLE_TREND_REVERSAL') {
    // For TP tracker, we reverse the logic
    base =
      tradeSide === 'open_long'
        ? 'BEARISH_CANDLE_TREND_REVERSAL'
        : tradeSide === 'open_short'
        ? 'BULLISH_CANDLE_TREND_REVERSAL'
        : base;
  }

  const simpleTypes = [
    "CANDLE_COLOR",
    "RSI_AND_SRSI_REVERSAL",
    "PROFIT_PERCENT",
    "SR_LEVEL",
    "BULLISH_CANDLE_TREND_REVERSAL",
    "BEARISH_CANDLE_TREND_REVERSAL",
    "SL_PERCENT",    
    "PREV_OPP_CANDLE_CLOSE_SL",
    "PREV_CANDLE_TREND_REVERSAL_SL",
    "PREV_LES_EDGE_CROSS_SL",    

  ];

  if (simpleTypes.includes(base)) {
    return base; // Direct base type
  }

  if (base === 'EMA' && CROSS_HIDDEN_EMA_OPTIONS.includes(value)) {
    return value;
  }

  if (!value || !crossMode || !tradeSide) return '';

  const dir = tradeSide === 'open_long' ? 'BEAR' : 'BULL';
  const crossPrefix =
    crossMode === 'FIRST_CANDLE_CROSS' ? 'FIRST_CANDLE' : 'CONFIRMED';
  return `${value}_${crossPrefix}_${dir}_CROSS`;
};


const onModalConfirm = async () => {
  setModalPhase('processing');
  setLoadingClose(true);
  try {
    if (closeMode === 'immediate') {
      const payload = {
        symbols: pendingSymbols,
        posCloseSRRequired: false
      };
      const res = await fetch(
        buildScopedApiUrl('/api/bot/ui/post/closeFilteredPositionWithSr', workspace),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
                      ...getApiHeaders() },
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json();
      setCloseResponse(data);
    } else if (closeMode === 'tracker') {

          // Validate selections for new tracker UI
    if (!selectedTimeFrame) {
      alert('Please select Time Frame');
      setModalPhase('confirm'); setLoadingClose(false); return;
    }
    if (!tpBase) {
      alert('Please select Tracker Base');
      setModalPhase('confirm'); setLoadingClose(false); return;
    }


    if (tpBase === 'PROFIT_PERCENT') {
        if (!tpTrackerValue) {
          alert('Please enter a profit percentage threshold');
          setModalPhase('confirm'); setLoadingClose(false); return;
        }
      } else if (tpBase === 'SL_PERCENT') {
        if (!tpTrackerValue) {
          alert('Please enter a Stop loss percentage threshold');
          setModalPhase('confirm'); setLoadingClose(false); return;
        }
      } else if (tpBase === 'SR_LEVEL') {
        if (!tpTrackerValue) {
          alert('Please enter a SR Level');
          setModalPhase('confirm'); setLoadingClose(false); return;
        }
      } else if (
        !['CANDLE_TREND_REVERSAL', 'CANDLE_COLOR', 'RSI_AND_SRSI_REVERSAL','PREV_OPP_CANDLE_CLOSE_SL','PREV_LES_EDGE_CROSS_SL','PREV_CANDLE_TREND_REVERSAL_SL','SWING_LEVEL_STOCH_RSI_REVERSAL','RSI_DIVERGENCE','AO_DIVERGENCE','FVG_TAGGED'].includes(tpBase)
      ) {
        // only check tpValue for types that need a selection
        if (!tpValue) {
          alert('Please select Tracker Value');
          setModalPhase('confirm'); setLoadingClose(false); return;
        }
      }


    const needsCross = ['EMA','VWAP','SWING_HIGH_LOW'].includes(tpBase) && !(tpBase === 'EMA' && CROSS_HIDDEN_EMA_OPTIONS.includes(tpValue));
    if (needsCross && !tpCross) {
      alert('Please select Candle Cross for the chosen base');
      setModalPhase('confirm'); setLoadingClose(false); return;
    }

          // Save tracker for ONLY the selected symbol
      if (!selectedSrSymbol) {
        alert('Please select a symbol for Take Profit Tracker.');
        setModalPhase('confirm');
        setLoadingClose(false);
        return;
      }

      const symbol = selectedSrSymbol;
      const tradeSide = getTradeSideForSymbol(symbol);
      if (!tradeSide) {
        alert(`Missing side for ${symbol}. Cannot create tracker.`);
        setModalPhase('confirm');
        setLoadingClose(false);
        return;
      }

      const composed = buildTpType(tpBase, tpValue, tpCross, tradeSide);
      if (!composed) {
        alert(`Unable to derive tracker type for ${symbol}.`);
        setModalPhase('confirm');
        setLoadingClose(false);
        return;
      }

      const row = {
        symbol,
        tradeSide,
        timeFrame: selectedTimeFrame,
        takeProfitType: composed,
        takeProfitTrackerStartedOn: new Date().toISOString(),
        expectedCandleColorToClose: '',
        takeProfitTrackerStoppedOn: '',
        activeFlag: 'Y',
        tpTrackerSource: 'PositionDetailsTable',
        trackerSpecialIdentifier, // Ã¢Å“â€¦ ensure field is included
        ...((tpBase === 'PROFIT_PERCENT' || tpBase === 'SR_LEVEL' || tpBase === 'SL_PERCENT' ) && { tpTrackerValue ,targetPercentage,})
      };

      await fetch(
        buildScopedApiUrl('/api/bot/ui/get/saveTakeProfitTrackers', workspace),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' ,
                      ...getApiHeaders()},
          body: JSON.stringify(row)
        }
      );

      setCloseResponse({ status: `Take Profit Tracker Created for ${symbol}` });

    } 
    
    else if (closeMode === 'stop_loss_sr') {

   // Restrict Trailing SR to one symbol only
   if (addTrailingSrFlag && pendingSymbols.length > 1) {
     alert('Trailing Stop Loss SR can only be applied to a single symbol at a time.');
     setModalPhase('confirm');
     return;
   }

    const selectedPos = displayedData.find(p => p.symbol === selectedSrSymbol);
    const avgOpen = parseFloat(selectedPos?.averageOpenPrice || 0);


   // Validation for Trailing SR
   if (addTrailingSrFlag) {
     const lvl = parseFloat(trailingSrLevel);
     if (!lvl || lvl <= 0) {
       alert('Enter a valid Trailing Stop Loss SR Level');
       setModalPhase('confirm'); setLoadingClose(false); return;
     }
     if (!trailingSrTimeFrame) {
       alert('Select Time Frame for Trailing Stop Loss SR');
       setModalPhase('confirm'); setLoadingClose(false); return;
     }
     const minRequired = avgOpen * 1.02;
     if (lvl < minRequired) {
       alert(`Trailing Stop Loss SR must be Ã¢â€°Â¥ 2% above Avg Open Price (Ã¢â€°Â¥ ${minRequired.toFixed(2)})`);
       setModalPhase('confirm'); setLoadingClose(false); return;
     }
   }

   // Validation for Break-Even SR
   if (addBreakEvenSrFlag && pendingSymbols.length === 0) {
     alert('Select at least one symbol for Break Even SR.');
     setModalPhase('confirm'); setLoadingClose(false); return;
   }
   if (addBreakEvenSrFlag && !breakEvenSrTimeFrame) {
     alert('Select Time Frame for Break Even SR');
     setModalPhase('confirm'); setLoadingClose(false); return;
   }

   // --- Save Stop Loss SRs ---
   const saveSr = async (symbol, srLevel, tf, comment) => {
     const tradeSide = getTradeSideForSymbol(symbol);
     if (!tradeSide) return;
     const srStrictType = tradeSide === 'open_long' ? 'CLOSE_BUY_ONLY' : 'CLOSE_SELL_ONLY';
     const payload = {
       coin: symbol,
       type: 'ICT_MANUAL_TRADE_SR',
       srLevel,
       zoneHigh: '',
       zoneLow: '',
       timeFrame: tf,
       strictFlag: true,
       srStrictType,
       crossModeForEntry: 'FIRST_CANDLE_CROSS',
       crossModeForExit: 'FIRST_CANDLE_CROSS',
       activeFlag: 'Y',
       status: '',
       levelTakenOutOn: null,
       comments: comment,
       attempt: 1
     };
     await fetch(
       buildScopedApiUrl('/api/bot/ui/get/saveSrDzSzLevels', workspace),
       {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' ,
                    ...getApiHeaders()},
         body: JSON.stringify(payload),
       }
     );
   };

   // 1Ã¯Â¸ÂÃ¢Æ’Â£ Trailing SR Ã¢â€ â€™ single symbol
// 1Ã¯Â¸ÂÃ¢Æ’Â£ Trailing SR Ã¢â€ â€™ single symbol
if (addTrailingSrFlag && trailingSrTimeFrame) {
  const success = [];
  const failed = [];

  const symbol = selectedSrSymbol || pendingSymbols[0];
  const pos = displayedData.find(p => p.symbol === symbol);
  if (!pos) {
    failed.push(symbol + ' Ã¢â‚¬â€ position not found');
  } else {
    const avg = parseFloat(pos.averageOpenPrice || 0);
    if (!avg) {
      failed.push(symbol + ' Ã¢â‚¬â€ invalid Avg Open Price');
    } else {

      const market = parseFloat(pos.marketPrice || 0);
      const profitPct = ((market - avg) / avg) * 100;

      const side = pos.holdSide === 'long' ? 'open_long' : 'open_short';

      // Require profit Ã¢â€°Â¥ 5% before adding trailing SR
      if (side === 'open_long' && profitPct < 5) {
        failed.push(`${symbol} Ã¢â‚¬â€ profit below 5% threshold`);
      }
      if (side === 'open_short' && profitPct > -5) {
        failed.push(`${symbol} Ã¢â‚¬â€ profit below 5% threshold`);
      }

      const computed = side === 'open_long'
        ? (avg * 1.02).toFixed(4)
        : (avg * 0.98).toFixed(4);

      const srLevel = trailingSrLevel || computed;

      // Validation check
      const minRequired = side === 'open_long'
        ? avg * 1.01
        : avg * 0.99;

      const lvl = parseFloat(srLevel);
      if (side === 'open_long' && lvl < minRequired) {
        failed.push(`${symbol} Ã¢â‚¬â€ below 2% threshold`);
      } else if (side === 'open_short' && lvl > minRequired) {
        failed.push(`${symbol} Ã¢â‚¬â€ below 2% threshold`);
      } else {
        await saveSr(
          symbol,
          srLevel,
          trailingSrTimeFrame,
          `Trailing Stop Loss SR (2%) added via PositionDetailsTable`
        );
        success.push(symbol);
      }
    }
  }

  setTrailingSrResultSummary({ success, failed });
}


   // 2Ã¯Â¸ÂÃ¢Æ’Â£ Break-Even SR Ã¢â€ â€™ multiple symbols (auto calc)
      // 2Ã¯Â¸ÂÃ¢Æ’Â£ Break-Even SR Logic
      if (addBreakEvenSrFlag && breakEvenSrTimeFrame) {
        const success = [];
        const failed = [];

        const targets = applyBreakEvenToAll
          ? displayedData.filter(p => pendingSymbols.includes(p.symbol))
          : displayedData.filter(p => p.symbol === selectedSrSymbol);

        for (const pos of targets) {
          const avg = parseFloat(pos.averageOpenPrice || 0);
          if (!avg) {
            failed.push(pos.symbol + ' Ã¢â‚¬â€ Invalid Open Price');
            continue;
          }

          const side = pos.holdSide === 'long' ? 'open_long' : 'open_short';
          const computed = side === 'open_long'
            ? (avg * 1.005).toFixed(4)
            : (avg * 0.995).toFixed(4);

          const srLevel = applyBreakEvenToAll
            ? computed
            : breakEvenSrLevel || computed;

          // Validation check
        // Compute current profit%
        const market = parseFloat(pos.marketPrice || 0);
        const profitPct = ((market - avg) / avg) * 100;

        // Required: profit Ã¢â€°Â¥ 2%
        if (side === 'open_long' && profitPct < 2) {
          failed.push(`${pos.symbol} Ã¢â‚¬â€ profit below 2% threshold`);
          continue;
        }
        if (side === 'open_short' && profitPct > -2) {
          failed.push(`${pos.symbol} Ã¢â‚¬â€ profit below 2% threshold`);
          continue;
        }

        // SR validation: must be Ã¢â€°Â¥ +0.5% (for longs) or Ã¢â€°Â¤ -0.5% (for shorts)
        const minRequired = side === 'open_long'
          ? avg * 1.004
          : avg * 0.996;

        const lvl = parseFloat(srLevel);
        if (side === 'open_long' && lvl < minRequired) {
          failed.push(`${pos.symbol} Ã¢â‚¬â€ SR below +0.5% level`);
          continue;
        }
        if (side === 'open_short' && lvl > minRequired) {
          failed.push(`${pos.symbol} Ã¢â‚¬â€ SR above -0.5% level`);
          continue;
        }


          await saveSr(
            pos.symbol,
            srLevel,
            breakEvenSrTimeFrame,
            `Break Even SR (0.5%) created via PositionDetailsTable`
          );
          success.push(pos.symbol);
        }

        setBreakEvenResultSummary({ success, failed });
      }

   setCloseResponse({ status: 'Stop Loss SR Levels Created' });
 }

    if (trailingSrResultSummary.success.length || breakEvenResultSummary.success.length) {
        alert('Ã¢Å“â€¦ SR levels created successfully.');
      }

    setModalPhase('response');
  } catch (e) {
    console.error(e);
    alert('Error processing request');
  } finally {
    setLoadingClose(false);
  }
};



  useEffect(() => {
    fetch(buildScopedApiUrl('/api/bot/ui/get/allOpenPositionsAlternative', workspace),
                    {
                      headers: getApiHeaders()
                    })
      .then(res => res.json())
      .then(data => {
        const normalizedPositions = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn('Unexpected open positions response shape for workspace', workspace, data);
        }
        setPositions(normalizedPositions);
        setPositionsNotice(
          normalizedPositions.length === 0 && !Array.isArray(data)
            ? 'Open positions are unavailable for the selected workspace right now.'
            : ''
        );
      })
      .catch((error) => {
        console.error(error);
        setPositions([]);
        setPositionsNotice('Open positions are unavailable for the selected workspace right now.');
      });
  }, [positionRefreshTrigger, workspace]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => setFilters({});

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  

  const total = positions.length;
  const longCount = positions.filter(pos => pos.holdSide === 'long').length;
  const shortCount = positions.filter(pos => pos.holdSide === 'short').length;
  const inProfit = positions.filter(pos => pos.unrealizedPL > 0).length;
  const inLoss = positions.filter(pos => pos.unrealizedPL < 0).length;
  const neutral = positions.filter(pos => pos.unrealizedPL === 0).length;
  const runningProfit = positions
  .filter(pos => parseFloat(pos.unrealizedPL) > 0)
  .reduce((acc, pos) => acc + parseFloat(pos.unrealizedPL), 0);

  const runningLoss = positions
  .filter(pos => parseFloat(pos.unrealizedPL) < 0)
  .reduce((acc, pos) => acc + parseFloat(pos.unrealizedPL), 0);


const filteredData = positions.filter(pos =>
  Object.entries(filters).every(([key, val]) => {
    if (!val) return true;

    if (
      (key === "unrealizedPL" || key === "margin") &&
      (/^[<>]=?/.test(val.trim()))
    ) {
      const trimmed = val.trim();
      const match = trimmed.match(/^([<>]=?)(-?\d+(\.\d+)?)/);
      if (match) {
        const operator = match[1];                
        const number = parseFloat(match[2]);       
        const posValue = parseFloat(pos[key]);    

        if (isNaN(posValue)) return false;
        switch (operator) {
          case "<":  return posValue <  number;
          case "<=": return posValue <= number;
          case ">":  return posValue >  number;
          case ">=": return posValue >= number;
          default:   return false;
        }
      }
      return false;
    }

    const field = pos[key]?.toString().toLowerCase() || "";
    return field.includes(val.toString().toLowerCase());
  })
);



  const symbolOptions = Array.from(
    new Set(positions.map((p) => p.symbol))
  ).sort(); 

  const sideOptions = Array.from(
    new Set(positions.map((p) => p.holdSide))
  ).sort();


  const sortedData = [...filteredData].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    const aVal = isNaN(a[key]) ? a[key] : parseFloat(a[key]);
    const bVal = isNaN(b[key]) ? b[key] : parseFloat(b[key]);
    return direction === 'asc' ? aVal > bVal ? 1 : -1 : aVal < bVal ? 1 : -1;
  });

  const displayedData = filteredBySelectionKeys.length > 0
  ? sortedData.filter(pos => {
      const rowKey = `${pos.symbol}|${pos.holdSide}|${pos.averageOpenPrice}`;
      return filteredBySelectionKeys.includes(rowKey);
    })
  : sortedData;

  useEffect(() => {
  if (!trailingSrLevel || !selectedSrSymbol) return;

  const position = displayedData.find(p => p.symbol === selectedSrSymbol);
  if (!position) return;

  const avgOpen = parseFloat(position.averageOpenPrice);
  const sr = parseFloat(trailingSrLevel);

  if (isFinite(avgOpen) && isFinite(sr) && avgOpen !== 0) {
    const pct = ((sr - avgOpen) / avgOpen) * 100;
    setTrailingSrChangePct(pct.toFixed(2));
  } else {
    setTrailingSrChangePct(null);
  }
}, [trailingSrLevel, selectedSrSymbol, displayedData]);



useEffect(() => {
  if (!breakEvenSrLevel || !selectedSrSymbol) return;

  const position = displayedData.find(p => p.symbol === selectedSrSymbol);
  if (!position) return;

  const avgOpen = parseFloat(position.averageOpenPrice);
  const sr = parseFloat(breakEvenSrLevel);

  if (isFinite(avgOpen) && isFinite(sr) && avgOpen !== 0) {
    const pct = ((sr - avgOpen) / avgOpen) * 100;
    setBreakEvenSrChangePct(pct.toFixed(2));
  } else {
    setBreakEvenSrChangePct(null);
  }
}, [breakEvenSrLevel, displayedData, selectedSrSymbol]);


  useEffect(() => {
  if (!addBreakEvenSrFlag || !selectedSrSymbol) return;

  const pos = displayedData.find(p => p.symbol === selectedSrSymbol);
  if (!pos) return;

  const avg = parseFloat(pos.averageOpenPrice || 0);
  const side = pos.holdSide === 'long' ? 'open_long' : 'open_short';

  if (isFinite(avg) && avg > 0) {
    const computed = side === 'open_long'
      ? (avg * 1.005).toFixed(4)
      : (avg * 0.995).toFixed(4);

    setBreakEvenSrLevel(computed);
  }
}, [addBreakEvenSrFlag, selectedSrSymbol, displayedData]);

useEffect(() => {
  if (tpBase !== 'SR_LEVEL' || !selectedSrSymbol) return;

  const pos = displayedData.find(p => p.symbol === selectedSrSymbol);
  if (!pos) return;

  // fetch live mark price for the selected symbol
  const fetchMarkPrice = async () => {
    try {
      const res = await fetch(
        buildApiUrl('/api/bot/get/getMarkPriceBySymbol', { symbol: selectedSrSymbol }),
        {
          headers: getApiHeaders()
        });
      const data = await res.json();
      const mark = parseFloat(data?.markPrice || pos.marketPrice || 0);
      if (mark > 0) {
        setTpTrackerValue(mark.toFixed(4));
        // compute SR vs Open %
        const open = parseFloat(pos.averageOpenPrice || 0);
        if (open > 0) {
          const pct = ((mark - open) / open) * 100;
          setTpSrChangePct(pct.toFixed(2));
        } else {
          setTpSrChangePct(null);
        }
      }
    } catch (err) {
      console.warn('Ã¢Å¡Â Ã¯Â¸Â Mark price fetch failed for SR_LEVEL', err);
    }
  };

  fetchMarkPrice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [tpBase, selectedSrSymbol]);



  const columns = [
    "symbol", "chart", "unrealizedPL", "holdSide", "margin", "marketPrice",
    "averageOpenPrice",  "changePct", "achievedProfits", "total", "locked", "available", "leverage"
  ];

  return (
    <div className="position-table-wrapper">

<div className="position-summary-container-flat">
  <h4>Position Summary</h4>
  {positionsNotice && (
    <p className="warning-text" style={{ marginBottom: '12px' }}>
      {positionsNotice}
    </p>
  )}

  <div className="position-summary-row-flat">
    <span className="position-summary-label">Total Positions:</span>
    <span className="position-summary-value total">{total}</span>
    <span className="position-summary-label">Long Positions:</span>
    <span className="position-summary-value">{longCount}</span>
    <span className="position-summary-label">Short Positions:</span>
    <span className="position-summary-value">{shortCount}</span>
    <span className="position-summary-label">In Profit:</span>
    <span className="position-summary-value profit">{inProfit}</span>
    <span className="position-summary-label">In Loss:</span>
    <span className="position-summary-value loss">{inLoss}</span>
    <span className="position-summary-label">Neutral:</span>
    <span className="position-summary-value">{neutral}</span>
  </div>
  <div className="position-summary-row-flat">
    <span className="position-summary-label">Running Profit ($):</span>
    <span className="position-summary-value profit">${runningProfit.toFixed(2)}</span>
    <span className="position-summary-label">Running Loss ($):</span>
    <span className="position-summary-value loss">${Math.abs(runningLoss).toFixed(2)}</span>
  </div>



</div>

      <div className="table-header">
        <h4>Open Positions Overview</h4>
         <div className="clear-all-option">
          <button className="position-action-btn position-action-btn--clear" onClick={clearFilters}>
            <span className="position-action-btn__icon" aria-hidden="true">Ã°Å¸Â§Â¹</span>
            <span>Clear Filters</span>
          </button>
          <button
            className="position-action-btn position-action-btn--multi"
            onClick={() => {
              if (isMultiSelectMode) {
                // Confirming selection: apply the filter
                setFilteredBySelectionKeys(selectedKeys);
                setSelectedKeys([]);
                setIsMultiSelectMode(false);
              } else if (filteredBySelectionKeys.length > 0) {
                // Clearing the selection-filter
                setFilteredBySelectionKeys([]);
              } else {
                // Enter multi-select mode
                setIsMultiSelectMode(true);
              }
            }}
          >
            <span className="position-action-btn__icon" aria-hidden="true">Ã¢Ëœâ€˜</span>
            {isMultiSelectMode
              ? 'Confirm Multi Select'
              : filteredBySelectionKeys.length > 0
                ? 'Clear Selection Filter'
                : 'Multi Select'}
          </button>
          {canClosePositions && (
            <button
              className="position-action-btn position-action-btn--close"
              onClick={handleCloseFilteredPositions}
              disabled={loadingClose || sortedData.length === 0}
            >
              <span className="position-action-btn__icon" aria-hidden="true">Ã°Å¸â€œâ€°</span>
              <span>Close Position</span>
            </button>
          )}
          <button
            className="position-action-btn position-action-btn--take-profit"
            onClick={fetchTpTrackers}
          >
            <span className="position-action-btn__icon" aria-hidden="true">ðŸŽ¯</span>
            <span>Take Profit Trackers</span>
          </button>

          {showTpTrackers && (
            <button
              className={`position-action-btn ${hideAllTrackers ? 'position-action-btn--show-trackers' : 'position-action-btn--hide-trackers'}`}
              onClick={() => {
                      setHideAllTrackers((prev) => {
                        const newHideState = !prev;
                        if (newHideState) {
                          // Hiding all trackers
                          setExpandedSymbols(new Set());
                        } else {
                          // Showing all trackers
                          const allTrackerKeys = new Set(Object.keys(groupedTpTrackers || {}));
                          setExpandedSymbols(allTrackerKeys);
                        }
                        return newHideState;
                      });
                      setShowTpTrackers(true); // ensure the main toggle remains active
                    }}
            >
              <span className="position-action-btn__icon" aria-hidden="true">
                {hideAllTrackers ? 'ðŸ‘' : 'ðŸ™ˆ'}
              </span>
              <span>{hideAllTrackers ? 'Show All Trackers' : 'Hide All Trackers'}</span>
            </button>
          )}



          {loadingClose && <span style={{ marginLeft: '8px' }}>Processing...</span>}
        </div>
      </div>
      <Modal
          isOpen={confirmModalOpen}
          onRequestClose={onModalCancel}
          className="yourModalClass position-close-modal"
          overlayClassName="yourOverlayClass position-close-overlay"
        >
        {modalPhase === 'confirm' && (
          <>
            <div className="position-close-modal__header">
              <span className="position-close-modal__eyebrow">Position Action</span>
              <h3>Close {pendingSymbols.length} positions?</h3>
            </div>

         <div className="modal-section position-close-mode-card">
          <div className="radio-row position-close-radio-row">
            <label>
              <input
                type="radio"
                value="immediate"
                checked={closeMode === 'immediate'}
                onChange={() => {
                  setCloseMode('immediate');
                  resetSrSection();
                }}
              />
              Close Immediately
            </label>
            <label>
              <input
                type="radio"
                value="tracker"
                checked={closeMode === 'tracker'}
                onChange={() => {
                    setCloseMode('tracker');
                    resetSrSection();
                  }}
              />
              Close By Take Profit Tracker
            </label>
          </div>

        </div>


           {closeMode === 'tracker' && (
          <div className="position-close-tracker-form">

              <div className="sr-field-row">
                <label className="sr-field-label">
                    Select Symbol:&nbsp;
                </label>
                  <select
                    value={selectedSrSymbol}
                    onChange={(e) => {
                      const sym = e.target.value;
                      setSelectedSrSymbol(sym);

                      const pos = displayedData.find((p) => p.symbol === sym);
                      if (!pos) return;
                    }}
                  >
                    <option value="">Select</option>
                    {pendingSymbols.map((sym) => (
                      <option key={sym} value={sym}>
                        {sym}
                      </option>
                    ))}
                  </select>
              </div>            

          {/* Base */}
            <div className="sr-field-row">
              <label className="sr-field-label">Tracker Base: </label>
              <select value={tpBase} onChange={onChangeTpBase}>
                {TP_BASE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            {/* Conditional value */}
            {tpBase === 'EMA' && (
              <div className="sr-field-row">
                <label className="sr-field-label">EMA:</label>
                <select value={tpValue} onChange={e => setTpValue(e.target.value)}>
                  <option value="">Select</option>
                  {EMA_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            )}
            {tpBase === 'VWAP' && (
              <div className="sr-field-row">
                <label className="sr-field-label">VWAP:</label>
                <select value={tpValue} onChange={e => setTpValue(e.target.value)}>
                  <option value="">Select</option>
                  {VWAP_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            )}
            {tpBase === 'SWING_HIGH_LOW' && (
             <div className="sr-field-row">
                <label className="sr-field-label">Swing:</label>
                <select value={tpValue} onChange={e => setTpValue(e.target.value)}>
                  <option value="">Select</option>
                  {SWING_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            )}
            {tpBase === 'CANDLE_COLOR' && (
              <div className="sr-field-row">
                <label className="sr-field-label">Candle Color:</label>
                <select value={tpValue} onChange={e => setTpValue(e.target.value)}>
                  <option value="">Select</option>
                  {CANDLE_COLOR_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            )}
            {(['RSI_AND_SRSI_REVERSAL', 'PREV_OPP_CANDLE_CLOSE_SL', 'PREV_CANDLE_TREND_REVERSAL_SL', 'PREV_LES_EDGE_CROSS_SL', 'SWING_LEVEL_STOCH_RSI_REVERSAL', 'RSI_DIVERGENCE', 'AO_DIVERGENCE', 'FVG_TAGGED'].includes(tpBase)) && (
              <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                No additional selection needed. Will use <code>{tpBase}</code>.
              </div>
            )}

           {tpBase === 'PROFIT_PERCENT' && (
                <div className="sr-field-row">
                  <label className="sr-field-label">Profit % Threshold:</label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    placeholder="e.g. 5 for 5%"
                    value={tpTrackerValue}
                    onChange={e => setTpTrackerValue(e.target.value)}
                  />
                </div>
              )}


            
          {tpBase === "SL_PERCENT" && (
           <div className="sr-field-row">
            <label>
              Stop Loss % Threshold:
              <input
                type="number"
                name="tpTrackerValue"
                value={tpTrackerValue}
                onChange={(e) => {
                  let val = Number(e.target.value);

                  // Ensure the value is ALWAYS negative
                  if (!isNaN(val)) {
                    if (val > 0) val = -Math.abs(val);
                    if (val === 0) val = ""; // empty allowed
                  }
                  setTpTrackerValue(val)
                }}
                placeholder="e.g. -5 for -5%"
              />
            </label>
          </div>
          )}  

              
            {tpBase === 'SR_LEVEL' && (
              <div className="sr-field-row">
                <label className="sr-field-label">SR Level:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      style={{ width: "140px" }}
                      value={tpTrackerValue}
                      placeholder="Auto-filled with mark price"
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setTpTrackerValue(e.target.value);

                        const pos = displayedData.find(p => p.symbol === selectedSrSymbol);
                        if (!pos || !pos.averageOpenPrice) return;

                        const avg = parseFloat(pos.averageOpenPrice);
                        if (!Number.isFinite(avg) || !Number.isFinite(val)) return;

                        const side = pos.holdSide === "long" ? "open_long" : "open_short";

                        // --- Calculate percentage change based on side ---
                        const pct =
                          side === "open_long"
                            ? ((val - avg) / avg) * 100
                            : ((avg - val) / avg) * 100;

                        // Ã¢Å“â€¦ Update both % states
                        setTpSrChangePct(pct.toFixed(2));
                        setTargetPercentage(pct.toFixed(2));

                        // Ã¢Å“â€¦ Auto-set trackerSpecialIdentifier
                        if (side === "open_long") {
                          setTrackerSpecialIdentifier(val > avg ? "TAKE_PROFIT_SR" : "STOP_LOSS_SR");
                        } else {
                          setTrackerSpecialIdentifier(val < avg ? "TAKE_PROFIT_SR" : "STOP_LOSS_SR");
                        }
                      }}
                    />

                  {tpSrChangePct !== null && (
                    <span
                      style={{
                        color: tpSrChangePct >= 0 ? "green" : "red",
                        fontWeight: "600",
                        fontSize: "13px",
                      }}
                    >
                      ({tpSrChangePct >= 0 ? "+" : ""}
                      {tpSrChangePct}%)
                    </span>
                  )}

                  {/* % adjustment input */}
                  <input
                    type="number"
                    step="0.1"
                    placeholder="+/-"
                    style={{ width: "60px", marginLeft: "6px" }}
                    value={targetPctAdjustment}
                    onChange={(e) => {
                      const adj = parseFloat(e.target.value);
                      setTargetPctAdjustment(e.target.value);
                      const pos = displayedData.find(p => p.symbol === selectedSrSymbol);
                      if (pos && pos.averageOpenPrice && Number.isFinite(adj)) {
                        const avg = parseFloat(pos.averageOpenPrice);
                        const side = pos.holdSide === "long" ? "open_long" : "open_short";
                        const newSr =
                          side === "open_long"
                            ? avg * (1 + adj / 100)
                            : avg * (1 - adj / 100);
                        setTpTrackerValue(newSr.toFixed(4));
                        setTargetPercentage(adj.toFixed(2));

                      // Ã¢Å“â€¦ Auto-set TAKE_PROFIT_SR or STOP_LOSS_SR (if user hasn't manually overridden)
                      if (
                        !trackerSpecialIdentifier ||
                        trackerSpecialIdentifier === "CUSTOM" ||
                        trackerSpecialIdentifier === "TAKE_PROFIT_SR" ||
                        trackerSpecialIdentifier === "STOP_LOSS_SR"
                      ) {
                        if (side === "open_long") {
                          setTrackerSpecialIdentifier(adj > 0 ? "TAKE_PROFIT_SR" : "STOP_LOSS_SR");
                        } else {
                          setTrackerSpecialIdentifier(adj > 0 ? "STOP_LOSS_SR" : "TAKE_PROFIT_SR");
                        }
                      }                        
                      }
                    }}
                  />
                  <span style={{ fontSize: "12px" }}>%</span>

                  {/* Ã¢Å“â€¦ Avg Open display */}
                  {selectedSrSymbol && (
                    <span
                      style={{
                        marginLeft: "10px",
                        fontSize: "12px",
                        color: "#777",
                      }}
                    >
                      Avg&nbsp;Open:&nbsp;
                      <strong style={{ color: "#444" }}>
                        {
                          displayedData.find(p => p.symbol === selectedSrSymbol)
                            ?.averageOpenPrice || "-"
                        }
                      </strong>
                    </span>
                  )}

                </div>
              </div>
            )}


            <div className="sr-field-row">
              <label className="sr-field-label">Tracker Special Identifier:</label>

              <div style={{ display: "flex", gap: "8px", alignItems: "center", flex: 1 }}>
                {/* Dropdown for known SR identifiers */}
                <select
                  value={trackerSpecialIdentifier}
                  onChange={(e) => setTrackerSpecialIdentifier(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="CUSTOM">Custom</option>
                  <option value="TAKE_PROFIT_SR">TAKE_PROFIT_SR</option>
                  <option value="STOP_LOSS_SR">STOP_LOSS_SR</option>
                  <option value="TRAILING_SL_SR">TRAILING_SL_SR</option>
                  <option value="BREAK_EVEN_SR">BREAK_EVEN_SR</option>
                </select>

                {/* Free text override for custom value */}
                <input
                  type="text"
                  placeholder="Or type custom identifier"
                  style={{ flex: 1 }}
                  value={
                    trackerSpecialIdentifier === "CUSTOM" ? "" : trackerSpecialIdentifier
                  }
                  onChange={(e) => setTrackerSpecialIdentifier(e.target.value)}
                />
              </div>
            </div>
            

            {/* Candle cross (disabled for excluded bases) */}
            <div className="sr-field-row">
              <label className="sr-field-label">Cross:</label>
              <select
                value={tpCross}
                onChange={e => setTpCross(e.target.value)}
                disabled={['CANDLE_COLOR','RSI_AND_SRSI_REVERSAL','SR_LEVEL','PROFIT_PERCENT','SL_PERCENT','CANDLE_TREND_REVERSAL','SWING_LEVEL_STOCH_RSI_REVERSAL','RSI_DIVERGENCE','AO_DIVERGENCE','FVG_TAGGED'].includes(tpBase) ||
                  (tpBase === 'EMA' && CROSS_HIDDEN_EMA_OPTIONS.includes(tpValue))}
              >
                <option value="FIRST_CANDLE_CROSS">FIRST_CANDLE_CROSS</option>
                <option value="CONFIRMED_CANDLE_CROSS">CONFIRMED_CANDLE_CROSS</option>
              </select>
            </div>



            {/* Time frame */}
            <div className="sr-field-row">
              <label className="sr-field-label">Time Frame: </label>
              <select
                value={selectedTimeFrame}
                onChange={(e) => setSelectedTimeFrame(e.target.value)}
              >
                <option value="">Select</option>
                {timeFrameOptions.map((tf) => (
                  <option key={tf} value={tf}>{tf}</option>
                ))}
              </select>
                </div>      
          </div>
        )}


          {closeMode === 'stop_loss_sr' && (
            <>
            {/* ---------------- STOP LOSS SR SECTION ---------------- */}
            
            <div style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
              <div className="sr-section-header">
                <span className="sr-icon-stoploss">Ã°Å¸â€ºâ€˜</span>
                <span>Stop Loss SR Settings</span>
              </div>

              <div className="sr-field-row">
                <label className="sr-field-label">
                    Select Symbol:&nbsp;
                </label>
                  <select
                    value={selectedSrSymbol}
                    onChange={(e) => {
                      const sym = e.target.value;
                      setSelectedSrSymbol(sym);

                      // auto compute SR level based on side + 2%
                      const pos = displayedData.find((p) => p.symbol === sym);
                      if (!pos) return;

                      const open = parseFloat(pos.averageOpenPrice || 0);
                      const side = pos.holdSide === 'long' ? 'open_long' : 'open_short';
                      const level =
                        side === 'open_long'
                          ? (open * 1.02).toFixed(4)
                          : (open * 0.98).toFixed(4);
                      setTrailingSrLevel(level);
                    }}
                  >
                    <option value="">Select</option>
                    {pendingSymbols.map((sym) => (
                      <option key={sym} value={sym}>
                        {sym}
                      </option>
                    ))}
                  </select>
              </div>

              {/* Trailing Stop Loss SR */}
              <div style={{ marginTop: 16 }}>                
              <div className="sr-section-header">
                <span className="sr-icon-breakeven">Ã°Å¸Å¸Â¢</span>
                <span>Trailing Stop Loss SR </span>
              </div>  

               <div className="sr-subtext">
                Trailing stop loss helps secure profits by adjusting SR dynamically as the price moves.
              </div>
                  <div className="inline-radio-group">
                    <label>
                      <input
                        type="radio"
                        name="addTrailingSrFlag"
                        checked={!addTrailingSrFlag}
                        onChange={() => {
                          setAddTrailingSrFlag(false);
                          setTrailingSrLevel('');
                        }}
                      />
                      No
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="addTrailingSrFlag"
                        checked={addTrailingSrFlag}
                        onChange={() => setAddTrailingSrFlag(true)}
                      />
                      Yes
                    </label>
                  </div>


                {addTrailingSrFlag && (
                  <div className="sr-input-block">
                    <PriceInfo
                      symbol={selectedSrSymbol}
                      displayedData={displayedData}
                      srLevel={trailingSrLevel} // or breakEvenSrLevel accordingly
                    />

                      <div className="sr-field-row">
                        <label className="sr-field-label">
                          SR Level (Ã¢â€°Â¥ 2% profit):
                        </label>
                          <input
                            type="number"
                            step="any"
                            placeholder="e.g. 64250.5"
                            value={trailingSrLevel}
                            onChange={(e) => setTrailingSrLevel(e.target.value)}
                            style={{ width: '150px', marginLeft: 8 }}
                          />
                      </div>                    

                      <div className="sr-field-row" style={{ marginTop: 8 }}>
                        <label className="sr-field-label">
                          Time Frame:
                        </label>
                        <select
                          value={trailingSrTimeFrame}
                          onChange={(e) => setTrailingSrTimeFrame(e.target.value)}
                          className="sr-field-input"
                        >
                          <option value="">Select</option>
                          {timeFrameOptions.map(tf => (
                            <option key={tf} value={tf}>{tf}</option>
                          ))}
                        </select>
                      </div>
                  </div>
                )}
              </div>
            
              {/* Break Even SR */}
              <div style={{ marginTop: 16 }}>
              <div className="sr-section-header">
                <span className="sr-icon-breakeven">Ã°Å¸Å¸Â¢</span>
                <span>Break Even SR: </span>
              </div>
              <div className="sr-subtext">
                Break Even SR locks minimal profit once price moves in your favor by 0.5%.
              </div>
                              
                <div className="inline-radio-group">
                  <label>
                    <input
                      type="radio"
                      name="addBreakEvenSrFlag"
                      checked={!addBreakEvenSrFlag}
                      onChange={() => {
                        setAddBreakEvenSrFlag(false);
                        setBreakEvenSrLevel('');
                        setApplyBreakEvenToAll(false);
                      }}
                    />
                    No
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="addBreakEvenSrFlag"
                      checked={addBreakEvenSrFlag}
                      onChange={() => setAddBreakEvenSrFlag(true)}
                    />
                    Yes
                  </label>
                </div>

                {addBreakEvenSrFlag && (
                  <div style={{ marginTop: 6, marginLeft: 20 }}>
                    <br/>
                    <div className="sr-field-row">
                      <label className="sr-field-label">
                        Apply Break Even SR to All Symbols:&nbsp;
                        </label>
                        <select
                          value={applyBreakEvenToAll ? 'YES' : 'NO'}
                          onChange={(e) => setApplyBreakEvenToAll(e.target.value === 'YES')}
                        >
                          <option value="NO">No</option>
                          <option value="YES">Yes</option>
                        </select>
                      
                    </div>

                    {!applyBreakEvenToAll && (
                      <>
                       
                       <div className="sr-input-block">
                          <PriceInfo
                            symbol={selectedSrSymbol}
                            displayedData={displayedData}
                            srLevel={breakEvenSrLevel}
                          />

                          <div className="sr-field-row">
                            <label className="sr-field-label">
                              SR Level (Ã¢â€°Â¥ 0.5% profit):
                            </label>
                            <input
                              type="number"
                              step="any"
                              placeholder="e.g. 64000.0"
                              value={breakEvenSrLevel}
                              onChange={(e) => setBreakEvenSrLevel(e.target.value)}
                              className="sr-field-input"
                            />
                          </div>
                        </div>

                      </>
                    )}

                   <div className="sr-field-row" style={{ marginTop: 8 }}>
                        <label className="sr-field-label">
                          Time Frame:
                        </label>
                        <select
                          value={breakEvenSrTimeFrame}
                          onChange={(e) => setBreakEvenSrTimeFrame(e.target.value)}
                          className="sr-field-input"
                        >
                          <option value="">Select</option>
                          {timeFrameOptions.map(tf => (
                            <option key={tf} value={tf}>{tf}</option>
                          ))}
                        </select>
                      </div>                      

                  </div>
                )}
              </div>

            </div>
          </>
          )}

            <div className="position-close-modal__actions">
              <button className="position-close-confirm-btn" onClick={onModalConfirm} disabled={loadingClose}>
                {loadingClose ? 'Processing...' : 'Confirm'}
              </button>
              <button className="position-close-cancel-btn" onClick={onModalCancel}>Cancel</button>
            </div>
          </>
        )}


            {modalPhase === 'processing' && (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Processing...</p>
              </div>
            )}

            {modalPhase === 'response' && (
              <div className="position-close-response">
                <div className="position-close-modal__header">
                  <span className="position-close-modal__eyebrow">Position Action Result</span>
                  <h3>Close Positions Response</h3>
                </div>
                <div className="position-close-response__body">
                {/* Ã¢Å“â€¦ Trailing Stop Loss SR Summary */}
                {trailingSrResultSummary.success.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <h5 style={{ color: 'green' }}>Ã¢Å“â€¦ Trailing Stop Loss SRs Added Successfully:</h5>
                    <ul>
                      {trailingSrResultSummary.success.map(sym => (
                        <li key={sym}>{sym}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {trailingSrResultSummary.failed.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <h5 style={{ color: 'red' }}>Ã¢ÂÅ’ Trailing Stop Loss SRs Failed (Validation Failed):</h5>
                    <ul>
                      {trailingSrResultSummary.failed.map(msg => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {breakEvenResultSummary.success.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <h5 style={{ color: 'green' }}>Ã¢Å“â€¦ Break Even SRs Added Successfully:</h5>
                  <ul>
                    {breakEvenResultSummary.success.map(sym => (
                      <li key={sym}>{sym}</li>
                    ))}
                  </ul>
                </div>
                )}

                  {breakEvenResultSummary.failed.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <h5 style={{ color: 'red' }}>Ã¢ÂÅ’ Failed to Add (Validation Failed):</h5>
                      <ul>
                        {breakEvenResultSummary.failed.map(msg => (
                          <li key={msg}>{msg}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                <ul className="position-close-response__list">
                  {Object.entries(closeResponse).map(([sym, msg]) => (
                    <li key={sym}>
                      <strong>{sym}</strong>
                      <pre>{typeof msg === 'string' ? msg : JSON.stringify(msg, null, 2)}</pre>
                    </li>
                  ))}
                </ul>
                </div>
                <div className="position-close-modal__actions">
                  <button className="position-close-cancel-btn" onClick={() => {
                    setTrailingSrResultSummary({ success: [], failed: [] });
                    setBreakEvenResultSummary({ success: [], failed: [] });
                    setConfirmModalOpen(false);
                    setModalPhase('confirm');
                    setCloseResponse(null);
                    setPendingSymbols([]);
                  }}>
                    Close
                  </button>
                </div>
              </div>
            )}

        </Modal>

        

      <table className="position-details-table">
           <thead>
            <tr>
              {isMultiSelectMode && <th>Select</th>}
              <th>S.No</th>
              {columns.map(col => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                >
                  {headers[col]}
                  {sortConfig.key === col
                    ? (sortConfig.direction === 'asc' ? ' ^' : ' v')
                    : ''}
                </th>
              ))}
            </tr>

            <tr>
              {isMultiSelectMode && <th></th>}
              <th></th>
              {columns.map(col => (
                <th key={`filter-${col}`}>
                  {col === "symbol" ? (
                    <select
                      value={filters[col] || ""}
                      onChange={e => handleFilterChange(col, e.target.value)}
                    >
                      <option value="">All Symbols</option>
                      {symbolOptions.map(sym => (
                        <option key={sym} value={sym}>{sym}</option>
                      ))}
                    </select>
                  ) : col === "chart" ? (
                    null
                  ) : col === "holdSide" ? (
                    <select
                      value={filters[col] || ""}
                      onChange={e => handleFilterChange(col, e.target.value)}
                    >
                      <option value="">All Sides</option>
                      {sideOptions.map(side => (
                        <option key={side} value={side}>{side}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder={
                        col === "unrealizedPL" || col === "margin"
                          ? `e.g. "<2" or ">10"`
                          : `Filter ${headers[col]}`
                      }
                      value={filters[col] || ""}
                      onChange={e => handleFilterChange(col, e.target.value)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
  {displayedData.map((pos, idx) => {
    const rowKey = `${pos.symbol}|${pos.holdSide}|${pos.averageOpenPrice}`;
    const positionTradeSide = toTradeSide(pos.holdSide);
    const positionTrackerKey = trackerGroupKey(pos.symbol, positionTradeSide);
    const trackersForPosition = groupedTpTrackers[positionTrackerKey] || [];
    const highlightRiskRow = trackersForPosition.length === 1;
    return (
      <>
        {/* Main position row */}
        
        <tr
            key={rowKey}
              onClick={e => {
                if (isMultiSelectMode && e.ctrlKey) {
                  toggleSelection(rowKey);
                }
              }}
              onDoubleClick={() => toggleSymbolExpansion(pos.symbol, positionTradeSide)}
              style={{
                cursor: showTpTrackers ? 'pointer' : 'default',
                backgroundColor: highlightRiskRow ? '#fff3cd' : 'transparent',
              }}
              className={selectedKeys.includes(rowKey) ? 'selected-row' : ''}
        >
          {isMultiSelectMode && (
            <td>
              <input
                type="checkbox"
                checked={selectedKeys.includes(rowKey)}
                onChange={() => toggleSelection(rowKey)}
                onClick={e => e.stopPropagation()} 
              />
            </td>
          )}
          <td>{idx + 1}</td>
          <td className={pos.symbol}>{pos.symbol}</td>
          <td className="position-chart-cell">
            <TradingViewChartIcon
              symbol={pos.symbol}
              timeFrame="15"
              exchange={workspace.exchange}
              marketType={workspace.marketType}
            />
          </td>
          <td
            className={
              parseFloat(pos.unrealizedPL) > 0
                ? 'blink-green'
                : parseFloat(pos.unrealizedPL) < 0
                ? 'blink-red'
                : ''
            }
          >
            {pos.unrealizedPL}
          </td>
          <td className={pos.holdSide === 'long' ? 'text-green' : 'text-red'}>
            {pos.holdSide}
          </td>
          <td>{pos.margin}</td>
          <td>{pos.marketPrice}</td>
          <td>{pos.averageOpenPrice}</td>
          <td
            className={
              (() => {
                const unrealizedPL = parseFloat(pos.unrealizedPL);
                if (isNaN(unrealizedPL) || unrealizedPL === 0) return '';
                return unrealizedPL > 0 ? 'text-green' : 'text-red';
              })()
            }
          >
            {(() => {
              const change = computeChangePct(pos.marketPrice, pos.averageOpenPrice, pos.holdSide);
              if (change == null || isNaN(change)) return '';
              const unrealizedPL = parseFloat(pos.unrealizedPL);
              const sign = !isNaN(unrealizedPL) && unrealizedPL > 0 ? '+' : '-';
              return `${sign}${Math.abs(change).toFixed(2)}%`;
            })()}
          </td>

          <td>{pos.achievedProfits}</td>
          <td>{pos.total}</td>
          <td>{pos.locked}</td>
          <td>{pos.available}</td>
          <td>{pos.leverage}</td>
        </tr>

        {/* Expanded TP Tracker rows below */}
        {showTpTrackers &&
          expandedSymbols.has(positionTrackerKey) &&
          groupedTpTrackers[positionTrackerKey] && (
            <tr className="tp-tracker-row">
              <td colSpan={columns.length + (isMultiSelectMode ? 2 : 1)}>
                <div className="tp-tracker-container">
                  <h5 style={{ marginBottom: '6px' }}>
                    Take Profit Trackers for {pos.symbol} ({pos.holdSide})
                  </h5>
                  <table className="tp-tracker-inner-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Type</th>
                        <th>Value</th>
                        <th>Target %</th>
                        <th>tracker Identifier</th>
                        <th>Time Frame</th>
                        <th>Tracker Source</th>
                        <th>Started</th>
                        <th>Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                    {groupedTpTrackers[positionTrackerKey].map((t, i) => {
                      const isTp = (t.trackerSpecialIdentifier || "").toUpperCase().includes("TAKE_PROFIT");
                      const isSl = (t.trackerSpecialIdentifier || "").toUpperCase().includes("STOP_LOSS");
                      const isBreakEven = (t.trackerSpecialIdentifier || "").toUpperCase() === "BREAK_EVEN_SR";

                      const rowStyle = {
                        backgroundColor: isTp
                          ? "rgba(0, 255, 0, 0.08)"
                          : isSl
                            ? "rgba(255, 0, 0, 0.08)"
                            : isBreakEven
                              ? "rgba(245, 158, 11, 0.14)"
                              : "transparent",
                      };

                      const targetColor = isTp ? "green" : isSl ? "red" : isBreakEven ? "#b45309" : "#222";

                    return (
                      <tr key={t.id || i} style={rowStyle}>
                        <td>{t.id}</td>
                        <td>{t.takeProfitType}</td>
                        <td>{t.tpTrackerValue || '-'}</td>
                        <td style={{ color: targetColor, fontWeight: 600 }}>
                          {t.targetPercentage ? `${t.targetPercentage}%` : '-'}
                        </td>
                        <td>{t.trackerSpecialIdentifier || '-'}</td>
                        <td>{t.timeFrame}</td>
                        <td>{t.tpTrackerSource}</td>
                        <td>{t.takeProfitTrackerStartedOn}</td>
                        <td>{t.comments || '-'}</td>
                      </tr>
                    );
                    })}

                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          )}
      </>
    );
  })}
</tbody>



      </table>

    </div>
  );
}



export default PositionDetailsTable;




